import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";

import { viewportCappedHeightSx } from "config";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CustomScrollbars from "../CustomScrollbars";
import { HighlightedText } from "../preview/searchUtils";
import CountChip from "./CountChip";
import {
  EMPTY_SSN_FILE_MESSAGE,
  formatFileSize,
  SSN_FILE_FILTERS,
  SSN_FLAG_LABELS,
  SSN_FLAG_TOOLTIPS,
  SsnFileRecordsResponse,
  SsnFileRowDto,
  SsnFileState,
} from "./ssnFile";
import {
  useLazyGetMajorJobSsnFileContentQuery,
  useLazyGetMajorJobSsnFileRecordsQuery,
} from "../../features/adminUsers/adminUsers";

const PAGE_SIZE = 300;
/** Past this the file does not fit in one API response - Download is the way. */
const RAW_MAX_BYTES = 4 * 1024 * 1024;

const SURFACE_SX = {
  bgcolor: "#ffffff",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  display: "flex",
  overflow: "hidden",
};

// HighlightedText marks live under this class; the file-preview provider defines
// it too, and this viewer is mounted outside that tree.
const searchMarkStyles = (
  <GlobalStyles
    styles={{
      "mark.preview-search-mark": {
        backgroundColor: "#ffe066",
        color: "inherit",
        padding: "0 1px",
        borderRadius: 2,
      },
    }}
  />
);

/**
 * One row per SSN: what is filed under it, and on click the entries themselves.
 *
 * The number is shown as a mask until the operator turns Show SSNs on. The last
 * four digits stay readable either way - that is the part the PDF Splitter puts
 * in folder names and the part an operator recognises an employee by.
 */
function SsnRow({ row, query }: { row: SsnFileRowDto; query: string }) {
  const [open, setOpen] = useState(false);
  const shown = row.ssn ?? row.ssn_masked;
  const title = row.names.length ? row.names.join(" / ") : "(no name)";
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        px: 1.5,
        py: 0.75,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          cursor: "pointer",
          minWidth: 0,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          rowGap: 0.5,
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center" }}>
          {open ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ fontFamily: "monospace", flexShrink: 0, fontWeight: 600 }}
        >
          <HighlightedText text={shown} query={query} />
        </Typography>
        <Box
          sx={{
            minWidth: 0,
            flexGrow: 1,
            // Once the row wraps, the name owns its own line and the ids move
            // below it instead of pushing the name out of existence.
            flexBasis: { xs: "100%", sm: "auto" },
          }}
        >
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            <HighlightedText text={title} query={query} />
          </Typography>
        </Box>
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
          {row.ids.slice(0, 4).map((identifier) => (
            <Chip
              key={identifier}
              size="small"
              variant="outlined"
              label={<HighlightedText text={identifier} query={query} />}
              sx={{ height: 20, fontSize: "0.68rem", fontFamily: "monospace" }}
            />
          ))}
          {row.ids.length > 4 ? (
            <Chip
              size="small"
              variant="outlined"
              label={`+${row.ids.length - 4}`}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          ) : null}
          {row.flags.map((flag) => (
            <Tooltip key={flag} title={SSN_FLAG_TOOLTIPS[flag]}>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={SSN_FLAG_LABELS[flag]}
                sx={{ height: 20, fontSize: "0.68rem" }}
              />
            </Tooltip>
          ))}
        </Stack>
      </Stack>
      {open ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.75,
            p: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            fontFamily: "monospace",
            fontSize: "0.72rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify({ [shown]: row.entries }, null, 2)}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * The SSN file of a major job as records, counters and shapes.
 *
 * It used to be handed to the browser as pretty-printed JSON, which answers none
 * of the questions an operator has of it - how many people are in it, whose
 * number carries two names, which ids two SSNs share - and, past six megabytes,
 * did not arrive at all (the API runs on Lambda). Same controls as the log
 * viewer: a search, filter chips, and counters that describe the whole file
 * rather than the rows on screen.
 */
export default function SsnFileView({
  majorJobId,
  heightSx = viewportCappedHeightSx(200, 480, 400),
  onDownload,
}: {
  majorJobId: number;
  /** Caps the scroll area; defaults to "as designed, but never past the viewport". */
  heightSx?: SxProps<Theme>;
  /** Download lives in the dialog's action bar; the empty state offers it too. */
  onDownload?: () => void;
}) {
  const [fetchRecords] = useLazyGetMajorJobSsnFileRecordsQuery();
  const [fetchContent] = useLazyGetMajorJobSsnFileContentQuery();

  const [state, setState] = useState<SsnFileState>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [reveal, setReveal] = useState(false);
  const [records, setRecords] = useState<SsnFileRowDto[]>([]);
  const [meta, setMeta] = useState<SsnFileRecordsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState(false);
  const [rawText, setRawText] = useState("");
  const [rawLoading, setRawLoading] = useState(false);
  const loadedRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(
    async (mode: "reset" | "more") => {
      if (mode === "more") setLoadingMore(true);
      else setLoading(true);
      try {
        const answer = await fetchRecords(
          {
            majorJobId,
            state,
            q: search || undefined,
            limit: PAGE_SIZE,
            offset: mode === "more" ? loadedRef.current : 0,
            reveal,
          },
          false,
        ).unwrap();
        setMeta(answer);
        setError(null);
        setRecords((prev) => {
          const next = mode === "more" ? [...prev, ...answer.records] : answer.records;
          loadedRef.current = next.length;
          return next;
        });
      } catch (e) {
        const message =
          (e as { data?: { message?: string } })?.data?.message ??
          "Failed to read the SSN file.";
        setError(message);
        if (mode === "reset") {
          setRecords([]);
          setMeta(null);
          loadedRef.current = 0;
        }
      } finally {
        if (mode === "more") setLoadingMore(false);
        else setLoading(false);
      }
    },
    [fetchRecords, majorJobId, reveal, search, state],
  );

  // Filters, search, reveal and the job itself all restart the list from the
  // first page: `load` is rebuilt exactly when one of them changes. The file is
  // written once at the end of a run, so there is nothing to poll for.
  useEffect(() => {
    loadedRef.current = 0;
    void load("reset");
  }, [load]);

  const size = meta?.file?.size_bytes ?? null;
  const rawTooLarge = size != null && size > RAW_MAX_BYTES;

  const loadRaw = useCallback(async () => {
    setRawLoading(true);
    try {
      const answer = await fetchContent(majorJobId, false).unwrap();
      let pretty = answer.content;
      try {
        pretty = JSON.stringify(JSON.parse(answer.content), null, 2);
      } catch {
        /* keep raw content when it is not valid JSON */
      }
      setRawText(pretty);
    } catch {
      setRawText("(failed to load the file as text - use Download)");
    } finally {
      setRawLoading(false);
    }
  }, [fetchContent, majorJobId]);

  useEffect(() => {
    if (!raw) return;
    void loadRaw();
  }, [raw, loadRaw]);

  const counts = meta?.counts;
  const file = meta?.file;
  const isEmptyFile = Boolean(file?.empty) || (counts != null && counts.ssns === 0);

  const refresh = () => {
    if (raw) {
      void loadRaw();
      return;
    }
    loadedRef.current = 0;
    void load("reset");
  };

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {searchMarkStyles}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search name, employee id, SSN digits..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ minWidth: { xs: "auto", sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSearchInput("")}
                />
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
          {SSN_FILE_FILTERS.filter(
            (filter) =>
              filter.value === "all" ||
              (counts?.[filter.value] ?? 0) > 0 ||
              state === filter.value,
          ).map((filter) => (
            <Chip
              key={filter.value}
              size="small"
              label={filter.label}
              onClick={() => setState(filter.value)}
              color={state === filter.value ? "primary" : "default"}
              variant={state === filter.value ? "filled" : "outlined"}
              sx={{ height: 24, fontSize: "0.72rem" }}
            />
          ))}
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        {meta?.can_reveal ? (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={reveal}
                onChange={(event) => setReveal(event.target.checked)}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Show SSNs
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        ) : null}
        <Tooltip
          title={
            rawTooLarge
              ? "The file is too large to show as text - use Download"
              : ""
          }
        >
          <span>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={raw}
                  disabled={rawTooLarge}
                  onChange={(event) => setRaw(event.target.checked)}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Raw
                </Typography>
              }
              sx={{ mr: 0 }}
            />
          </span>
        </Tooltip>
        <Button
          size="small"
          startIcon={
            loading || rawLoading ? <CircularProgress size={14} /> : <RefreshIcon />
          }
          disabled={loading || rawLoading}
          onClick={refresh}
        >
          Refresh
        </Button>
      </Stack>

      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        <CountChip
          label="SSNs"
          value={counts?.ssns ?? 0}
          tooltip="Numbers in the file - one per employee the run found"
        />
        <CountChip
          label="Entries"
          value={counts?.entries ?? 0}
          tooltip="Lookup keys: most vendors file one employee under two or three ids"
        />
        <CountChip
          label="Names"
          value={counts?.names ?? 0}
          tooltip="Distinct employee names across the file"
        />
        {(counts?.several_names ?? 0) > 0 ? (
          <CountChip
            label={SSN_FLAG_LABELS.several_names}
            value={counts?.several_names ?? 0}
            color="warning"
            tooltip={SSN_FLAG_TOOLTIPS.several_names}
          />
        ) : null}
        {(counts?.no_name ?? 0) > 0 ? (
          <CountChip
            label={SSN_FLAG_LABELS.no_name}
            value={counts?.no_name ?? 0}
            color="warning"
            tooltip={SSN_FLAG_TOOLTIPS.no_name}
          />
        ) : null}
        {(counts?.no_id ?? 0) > 0 ? (
          <CountChip
            label={SSN_FLAG_LABELS.no_id}
            value={counts?.no_id ?? 0}
            color="warning"
            tooltip={SSN_FLAG_TOOLTIPS.no_id}
          />
        ) : null}
        {(counts?.invalid_ssn ?? 0) > 0 ? (
          <CountChip
            label={SSN_FLAG_LABELS.invalid_ssn}
            value={counts?.invalid_ssn ?? 0}
            color="warning"
            tooltip={SSN_FLAG_TOOLTIPS.invalid_ssn}
          />
        ) : null}
        {(counts?.shared_id ?? 0) > 0 ? (
          <CountChip
            label={SSN_FLAG_LABELS.shared_id}
            value={counts?.shared_id ?? 0}
            color="warning"
            tooltip={SSN_FLAG_TOOLTIPS.shared_id}
          />
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {file ? (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", alignSelf: "center" }}
          >
            {[
              file.vendor,
              formatFileSize(file.size_bytes),
              file.last_modified ? new Date(file.last_modified).toLocaleString() : "",
            ]
              .filter(Boolean)
              .join(" - ")}
          </Typography>
        ) : null}
      </Stack>

      {meta?.id_shapes?.length ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {`Employee id shapes: ${meta.id_shapes.join(", ")}`}
        </Typography>
      ) : null}

      {isEmptyFile && !error ? (
        <Alert
          severity="warning"
          action={
            onDownload ? (
              <Button size="small" color="inherit" onClick={onDownload}>
                Download
              </Button>
            ) : undefined
          }
        >
          {EMPTY_SSN_FILE_MESSAGE}
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!isEmptyFile || raw ? (
        <Box sx={{ ...SURFACE_SX, ...heightSx }}>
          <CustomScrollbars>
            {raw ? (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {rawLoading ? "(loading...)" : rawText}
              </Box>
            ) : records.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", p: 2, textAlign: "center" }}
              >
                {loading
                  ? "(loading...)"
                  : counts?.ssns
                    ? "No rows match the filters."
                    : "(empty)"}
              </Typography>
            ) : (
              records.map((row) => (
                <SsnRow key={row.ssn_masked + row.ids.join()} row={row} query={search} />
              ))
            )}
          </CustomScrollbars>
        </Box>
      ) : null}

      {!raw && !isEmptyFile ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {`Showing ${records.length} of ${meta?.total_matched ?? 0}`}
          </Typography>
          {meta?.has_more ? (
            <Button
              size="small"
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
              onClick={() => void load("more")}
            >
              Load more
            </Button>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          {meta?.revealed ? (
            <Typography variant="caption" sx={{ color: "warning.dark" }}>
              full numbers shown
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              numbers masked
            </Typography>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
}
